import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { faqs, howItWorks } from "./helpers";
import { vendorUUIDs } from "../../constants";

import SeatMapImage from "public/images/iowacubs/24_Attend Map@2x.png";
import MobileFindGamesBanner from "public/images/iowacubs/Iowa Cubs 24_Attend_350x180.jpg";
import FindGamesBanner from "public/images/iowacubs/25_Attend Graphics-TRIPLE PLAY PACK_1486x148.png";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/iowa-cubs-transparent.png";

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "iowacubs_flex",
  browserTitle: "Cubs Build Your Own Plan",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Iowa Cubs",

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        minSelectedAmount: 3,
        updatedAccountGroup: 0,
        discount: 33,
        customProgressBarMessage:
          "Congrats! You've unlocked 33% off your tickets! Add 1 more game and receive 44% off or 2 more games for 55% tickets! ",
      },
      {
        id: 2,
        minSelectedAmount: 4,
        updatedAccountGroup: 1,
        discount: 44,
        customProgressBarMessage:
          "Congrats! You have unlocked 44% off your seats! Add 1 more games for 55% off or checkout now.",
      },
      {
        id: 3,
        minSelectedAmount: 5,
        updatedAccountGroup: 2,
        discount: 55,
        customProgressBarMessage:
          "Congrats! You have unlocked 55% off your seats! Add more games or checkout now.",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    mapId: "",
    venueId: "",
    isView3dButtonHidden: true,
    staticSeatMapImage: SeatMapImage.src,
    isShowAvailableSeatsButtonHidden: true,
    customSeatMapStaticImageStyle: {
      width: "70%",
      marginX: "auto",
      display: "block",
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 3,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: 3,
    allowedPaymentFailCount: 3,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl:
    "https://mlb.tickets.com/?agency=IWCM_MYTIXX&orgid=56738#/auth/login",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    findGamesBannerImage: FindGamesBanner.src,
    authBackgroundImage: FindGamesBanner.src,
    checkoutStatusPageImage: FindGamesBanner.src,
    logoImage: LogoImage + "?width=128&height=66",
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: "",
    mobileFindGamesBannerImage: MobileFindGamesBanner.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:5152436111"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (515) 243-6111
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#005596",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      similarSeatModalBody: (seatTitle: string) => (
        <>
          We are going to apply the same or similar seats to all games in your
          pack. Seat locations will be visible on the checkout page.
        </>
      ),
      progressBarText:
        "Add 3 games and get 33% off, add 3 games and get 44% off or add 5 games and get 55% off your tickets!",
      progressBarAditionalText: " to continue!",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your Iowa Cubs tickets are confirmed! Tickets will be available in your Iowa Cubs Account Manager shortly.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
